import { Injectable, Injector, runInInjectionContext } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';

import { AuthGuard } from '../guards/auth.guard';

enum Claim {
  FirstName = 'given_name',
  LastName = 'family_name',
  Email = 'email',
  UserId = 'sub',
  IdentityProvider = 'idp',
  TenantId = 'tid',
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(private injector: Injector) {}

  private get oAuthService(): OAuthService {
    // runInInjectionContext ensures any use of `inject()` inside the OAuthService factory
    // runs inside a valid injection context (required in Angular 17+).
    return runInInjectionContext(this.injector, () => this.injector.get(OAuthService));
  }

  isAuthenticated() {
    return this.oAuthService.hasValidIdToken();
  }

  async isAuthenticatedAnywhere() {
    if (this.isAuthenticated()) {
      return true;
    }

    try {
      await this.silentRefresh();

      return this.isAuthenticated();
    } catch {
      return false;
    }
  }

  private getClaim(claim: Claim): string {
    const noClaimValue = '';
    const claims = this.oAuthService.getIdentityClaims();
    if (claims) {
      const claimValue = claims[claim];
      if (claimValue) {
        return claimValue;
      }
    }
    return noClaimValue;
  }

  getCurrentUserName(): string {
    const firstName = this.getClaim(Claim.FirstName);
    const lastName = this.getClaim(Claim.LastName);
    return `${firstName} ${lastName}`.trim() || 'Guest';
  }

  getCurrentUserEmail() {
    return this.getClaim(Claim.Email);
  }

  getCurrentLoggedOnUserId() {
    return this.getClaim(Claim.UserId);
  }

  getIdentityProvider() {
    return this.getClaim(Claim.IdentityProvider);
  }

  getTenantId() {
    return this.getClaim(Claim.TenantId);
  }

  init(returnUrl?: string, params?: { [key: string]: string }) {
    if (params) {
      this.oAuthService.customQueryParams = params;
    }

    const loginHint = this.getCurrentUserEmail();

    this.oAuthService.initImplicitFlow(returnUrl || location.href, loginHint);
  }

  logOutLocal() {
    this.oAuthService.logOut(true);
  }

  logOut(returnUrl = '') {
    this.oAuthService.logOut(false, returnUrl);
  }

  logOutWithParams(customParams) {
    this.oAuthService.logOut(customParams);
  }

  resetImplicitFlow() {
    this.oAuthService.resetImplicitFlow();
  }

  async silentRefresh(url = '') {
    try {
      await this.oAuthService.silentRefresh();
    } catch (err) {
      const errorResponsesRequiringUserInteraction = [
        'interaction_required',
        'login_required',
        'account_selection_required',
        'consent_required',
      ];

      if (
        err?.reason?.params?.error &&
        errorResponsesRequiringUserInteraction.includes(err.reason.params.error)
      ) {
        const { returnUrl, customQueryParams } = AuthGuard.getReturnUrlAndCustomQueryParams(
          url || location.href,
        );
        this.oAuthService.initLoginFlow(returnUrl, customQueryParams);
        return;
      }

      throw err;
    }
  }

  getAccessToken() {
    return this.oAuthService.getAccessToken();
  }

  getAccessTokenExpiration() {
    return this.oAuthService.getAccessTokenExpiration();
  }
}
